/**
 * Collection Filters JavaScript
 *
 * Features:
 * - Accordion toggle (expand/collapse filter groups)
 * - Mobile drawer open/close functionality
 * - Body scroll lock when drawer is open
 * - Focus trap in drawer
 * - Swipe gesture detection for closing drawer
 * - Click backdrop to close
 * - Escape key to close
 * - Reset individual filters
 * - Reset all filters
 * - Form submission (page refresh) - NO AJAX in Step 1
 *
 * @version 1.0.0
 */

class CollectionFilters {
  constructor() {
    this.drawer = document.querySelector('[data-filter-drawer]');
    this.drawerTrigger = document.querySelector('[data-filter-drawer-trigger]');
    this.drawerPanel = this.drawer?.querySelector('[data-filter-drawer-panel]');
    this.drawerBackdrop = this.drawer?.querySelector('[data-filter-drawer-backdrop]');
    this.drawerCloseBtn = this.drawer?.querySelector('[data-filter-drawer-close]');
    this.drawerApplyBtn = this.drawer?.querySelector('[data-filter-drawer-apply]');
    this.drawerContent = this.drawer?.querySelector('[data-filter-drawer-content]');
    this.mobileForm = document.getElementById('filter-form-mobile');

    // Touch tracking for swipe
    this.touchStartX = 0;
    this.touchEndX = 0;

    // Focus trap elements
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;

    // Breakpoint constant (Tailwind lg breakpoint)
    this.DESKTOP_BREAKPOINT = 1024;

    // Resize handler reference
    this.resizeTimeout = null;

    this.init();
  }

  init() {
    this.bindEvents();
    this.initAccordions();
    this.initPriceFilters();
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Mobile drawer trigger
    this.drawerTrigger?.addEventListener('click', () => this.openDrawer());

    // Close drawer events
    this.drawerCloseBtn?.addEventListener('click', () => this.closeDrawer());
    this.drawerBackdrop?.addEventListener('click', () => this.closeDrawer());
    // Note: "View Results" button click is now handled by CollectionFiltersAjax class

    // Escape key to close drawer
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer?.classList.contains('active')) {
        this.closeDrawer();
      }
    });

    // Swipe to close
    if (this.drawerPanel) {
      this.drawerPanel.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
      this.drawerPanel.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
    }

    // Focus trap
    this.drawer?.addEventListener('keydown', (e) => this.handleFocusTrap(e));

    // Window resize - close drawer on desktop breakpoint
    window.addEventListener('resize', () => this.handleResize());
  }

  /**
   * Initialize accordion functionality for filter groups
   */
  initAccordions() {
    // Desktop accordions (all open by default)
    const desktopToggles = document.querySelectorAll('#collection-filters-desktop [data-filter-toggle]');
    desktopToggles.forEach(toggle => {
      toggle.addEventListener('click', () => this.toggleAccordion(toggle, false));
    });

    // Mobile accordions (all closed by default)
    const mobileToggles = document.querySelectorAll('.collection-filters-drawer-group [data-filter-toggle]');
    mobileToggles.forEach(toggle => {
      toggle.addEventListener('click', () => this.toggleAccordion(toggle, true));
    });
  }

  /**
   * Toggle accordion open/close
   * @param {HTMLElement} toggle - The toggle button element
   * @param {boolean} isMobile - Whether this is a mobile accordion
   */
  toggleAccordion(toggle, isMobile) {
    const filterGroup = toggle.closest(isMobile ? '.collection-filters-drawer-group' : '.collection-filters-group');
    const content = filterGroup.querySelector('[data-filter-content]');
    const isCollapsed = filterGroup.getAttribute('data-collapsed') === 'true';

    if (isCollapsed) {
      // Expand
      filterGroup.setAttribute('data-collapsed', 'false');
      toggle.setAttribute('aria-expanded', 'true');

      if (isMobile) {
        content.classList.remove('hidden');
      } else {
        content.style.maxHeight = content.scrollHeight + 'px';
      }
    } else {
      // Collapse
      filterGroup.setAttribute('data-collapsed', 'true');
      toggle.setAttribute('aria-expanded', 'false');

      if (isMobile) {
        content.classList.add('hidden');
      } else {
        content.style.maxHeight = '0';
      }
    }
  }

  /**
   * Open mobile filter drawer
   */
  openDrawer() {
    if (!this.drawer) return;

    // Show drawer
    this.drawer.classList.remove('hidden');
    this.drawer.style.display = 'block';

    // Trigger reflow for animation
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Add active class for animation
        this.drawer.classList.add('active');
      });
    });

    // Lock body scroll
    document.body.classList.add('collection-filters-drawer-open');

    // Set up focus trap
    this.setupFocusTrap();

    // Focus the close button
    setTimeout(() => {
      this.drawerCloseBtn?.focus();
    }, 300);

    // Announce to screen readers
    // TODO: Use data attribute for i18n when implementing AJAX
    this.announce('Filter drawer opened');
  }

  /**
   * Close mobile filter drawer
   */
  closeDrawer() {
    if (!this.drawer) return;

    // Remove active class
    this.drawer.classList.remove('active');

    // Unlock body scroll
    document.body.classList.remove('collection-filters-drawer-open');

    // Hide drawer after animation
    setTimeout(() => {
      this.drawer.classList.add('hidden');
      this.drawer.style.display = 'none';
    }, 300);

    // Return focus to trigger button
    this.drawerTrigger?.focus();

    // Announce to screen readers
    // TODO: Use data attribute for i18n when implementing AJAX
    this.announce('Filter drawer closed');
  }

  /**
   * Apply filters from mobile drawer (submit form)
   */
  applyFilters() {
    if (this.mobileForm) {
      this.mobileForm.submit();
    }
  }

  /**
   * Handle touch start for swipe detection
   * @param {TouchEvent} e - Touch event
   */
  handleTouchStart(e) {
    this.touchStartX = e.changedTouches[0].screenX;
  }

  /**
   * Handle touch end for swipe detection
   * @param {TouchEvent} e - Touch event
   */
  handleTouchEnd(e) {
    this.touchEndX = e.changedTouches[0].screenX;
    this.handleSwipe();
  }

  /**
   * Detect swipe direction and close drawer if swiping right
   */
  handleSwipe() {
    const swipeThreshold = 100; // Minimum distance for swipe
    const swipeDistance = this.touchEndX - this.touchStartX;

    // Swipe right to close (drawer slides from right)
    if (swipeDistance > swipeThreshold) {
      this.closeDrawer();
    }
  }

  /**
   * Setup focus trap in drawer
   */
  setupFocusTrap() {
    if (!this.drawer) return;

    const focusableSelector = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    this.focusableElements = Array.from(this.drawer.querySelectorAll(focusableSelector));
    this.firstFocusableElement = this.focusableElements[0];
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
  }

  /**
   * Handle focus trap keyboard navigation
   * @param {KeyboardEvent} e - Keyboard event
   */
  handleFocusTrap(e) {
    if (!this.drawer?.classList.contains('active')) return;

    const isTabPressed = e.key === 'Tab';
    if (!isTabPressed) return;

    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === this.firstFocusableElement) {
        e.preventDefault();
        this.lastFocusableElement?.focus();
      }
    } else {
      // Tab
      if (document.activeElement === this.lastFocusableElement) {
        e.preventDefault();
        this.firstFocusableElement?.focus();
      }
    }
  }

  /**
   * Handle window resize - close drawer when crossing to desktop breakpoint
   */
  handleResize() {
    // Debounce resize events
    clearTimeout(this.resizeTimeout);

    this.resizeTimeout = setTimeout(() => {
      const isDesktop = window.innerWidth >= this.DESKTOP_BREAKPOINT;
      const isDrawerActive = this.drawer?.classList.contains('active');

      // Auto-close drawer when resizing to desktop
      if (isDesktop && isDrawerActive) {
        this.closeDrawer();
      }
    }, 150);
  }

  /**
   * Initialize price filter radio button handlers
   */
  initPriceFilters() {
    // Desktop price filter
    const desktopPriceRadios = document.querySelectorAll('[data-price-radio]');
    const desktopMinInput = document.querySelector('[data-price-min]');
    const desktopMaxInput = document.querySelector('[data-price-max]');
    const desktopForm = document.getElementById('filter-form');

    desktopPriceRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        if (e.target.checked) {
          const min = e.target.getAttribute('data-min');
          const max = e.target.getAttribute('data-max');

          if (desktopMinInput && desktopMaxInput) {
            // Convert cents to dollars for Shopify filter API
            desktopMinInput.value = (parseInt(min) / 100).toString();
            desktopMaxInput.value = (parseInt(max) / 100).toString();
            desktopMinInput.disabled = false;
            desktopMaxInput.disabled = false;
          }

          // Submit form immediately on desktop - DISABLED for AJAX (Step 2)
          // Will be handled by AJAX in Step 7
          // if (desktopForm) {
          //   desktopForm.submit();
          // }
        }
      });
    });

    // Mobile price filter
    const mobilePriceRadios = document.querySelectorAll('[data-price-radio-mobile]');
    const mobileMinInput = document.querySelector('[data-price-min-mobile]');
    const mobileMaxInput = document.querySelector('[data-price-max-mobile]');

    mobilePriceRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        if (e.target.checked) {
          const min = e.target.getAttribute('data-min');
          const max = e.target.getAttribute('data-max');

          if (mobileMinInput && mobileMaxInput) {
            // Convert cents to dollars for Shopify filter API
            mobileMinInput.value = (parseInt(min) / 100).toString();
            mobileMaxInput.value = (parseInt(max) / 100).toString();
            mobileMinInput.disabled = false;
            mobileMaxInput.disabled = false;
          }

          // Don't auto-submit on mobile - user clicks "View Results"
        }
      });
    });
  }

  /**
   * Announce to screen readers
   * @param {string} message - Message to announce
   */
  announce(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    document.body.appendChild(announcement);

    setTimeout(() => {
      announcement.remove();
    }, 1000);
  }
}

/**
 * Initialize collection filters when DOM is ready
 */
document.addEventListener('DOMContentLoaded', () => {
  window.collectionFilters = new CollectionFilters();
});

/**
 * Reinitialize if page is loaded via Shopify's theme editor
 */
if (Shopify?.designMode) {
  document.addEventListener('shopify:section:load', () => {
    window.collectionFilters = new CollectionFilters();
  });
}
